/**
 * @whatItDoes This service acts on HTTP requests in the interceptor chain to add
 * the UZURV authentication token to outgoing requests to the UZURV API. See
 * https://angular.io/api/common/http/HttpInterceptor for more information on HTTP Inteceptors
 *
 * TODO: Find out why some paths aren't reporting coverage in istanbul
 */
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BugsnagService, HttpException } from '@uzurv/core';
import { Observable, throwError } from 'rxjs';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UZURV_REST_CONFIGURATION } from '../../interfaces/uzurv-rest-configuration';
import { AuthTokenService } from '../../services/auth-token/auth-token.service';
import { AuthDataService } from '../../services/authentication/auth-data.service';

@Injectable()
export class UzurvAuthTokenHttpInterceptor implements HttpInterceptor {
  constructor(
    private authTokenService: AuthTokenService,
    private authDataService: AuthDataService,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    private bugsnagService: BugsnagService
  ) {}

  /**
   * Implementing intercept() takes in the request to be handled and if
   * an authentication token exists in localStorage, attaches it to the
   * headers in the correct field
   *
   * @param request the outgoing HttpRequest
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authTokenService.getAuthToken();

    if (!this.shouldInterceptRequest(request)) {
      return next.handle(request);
    }

    /* istanbul ignore else */
    if (!token) {
      this.bugsnagService.leaveBreadcrumb('Missing auth token', {
        currentUserAuthData: this.authDataService.currentUserAuthData,
        url: request.url
      });
      return next.handle(request);
    }

    // Throw a 401 if the auth token is expired
    if (this.authTokenService.isAuthTokenExpired()) {
      return throwError(new HttpException(401, 'Auth Token Expired'));
    }

    /* istanbul ignore next */
    request = request.clone({
      headers: request.headers.set('x-auth-token', token)
    });

    /**
     * If handling the request produces 401: Unauthorized, then the
     * token isn't valid and we should clear it
     *
     * This is covered, but istanbul doesn't know that so...
     */
    /* istanbul ignore next */
    return next.handle(request);
  }

  /**
   * Checks the request url to determine whether or not this interceptor should intercept
   *
   * @param request
   */
  private shouldInterceptRequest(request: HttpRequest<any>): boolean {
    if (
      !request.url.trim().startsWith(this.uzurvRestConfiguration.claraApiUrl) &&
      !request.url.trim().startsWith(this.uzurvRestConfiguration.dotsApiUrl) &&
      !request.url.trim().startsWith(this.uzurvRestConfiguration.reportingApiUrl)
    ) {
      return false;
    }

    // Login
    if (request.url.trim().endsWith('login')) {
      return false;
    }

    // Verify email and password reset
    if (request.url.trim().includes('/verifyEmail/')) {
      return false;
    }

    if (request.url.trim().includes('/verifyEmailAndRequestReset/')) {
      return false;
    }

    if (request.url.trim().includes('/resetPassword/')) {
      return false;
    }

    return true;
  }
}
